/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import { fetchBestSellerData } from '@/apis/bestSeller/apiBestSeller';
import { useTranslation } from '@/i18n/client';
import { useInViewport } from 'ahooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PageBanner from './components/PageBanner';
import styles from './style.module.scss';

import RecommendCard from './components/recommendCard';
import { sendPv } from '@/tracks/32520';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/32520';
import { debounce } from 'lodash';

import UKLoadingIcon from '@/components/UKLoadingIcon';
import {
  BESTSELLERS_EVENT_NAMES,
  bestSellersBusinessLogger,
} from '@/utils/logger/instance/bestSellersBusinessLogger';

export interface IBestSellerClientProps {
  localInfo: any;
  bestSellerCategoryData: any[];
  sidebarData: any;
  bannerDesc: string;
  bannerBackGround: string | null;
  initPage: number;
  total: number;
  initSequenceId: string;
}

export default function BestSellerClient({
  bestSellerCategoryData,
  localInfo,
  bannerDesc,
  bannerBackGround,
  sidebarData,
  initPage,
  initSequenceId,
  total,
}: IBestSellerClientProps) {
  // @ts-ignore
  const [datas, setDatas] = useState<any[]>(bestSellerCategoryData?.data as any[]);
  const [currentPage, setCurrentPage] = useState<number>(initPage);
  const { t } = useTranslation();
  // -------------- 分页用 -------------------- //
  // Sentinel detects line
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Determine last page
  const [lastPage, setLastPage] = useState(datas.length >= total);
  // Set current page total page
  const [totalPage, setTotalPage] = useState(total);
  // Set current page sequenceId
  const [currentSequenceId, setCurrentSequenceId] = useState(initSequenceId);
  // client Ready
  const [isClient, setIsClient] = useState<boolean>(false);
  // loading
  const [loading, setLoading] = useState<boolean>(false);
  // useViewPort
  const [isVisible, ratio] = useInViewport(isClient ? sentinelRef.current : undefined);

  // -------------- pv -------------------- //
  useEffect(() => {
    // 排行榜首页浏览
    sendPv.Activity_BestSellersHome();
    // isclientready
    setIsClient(true);
  }, []);

  // -------------- debounce -------------------- //
  useEffect(() => {
    if (isVisible) {
      if (currentPage < totalPage) {
        debouncedHandleScroll();
      }
    }
  }, [isVisible]);

  // -------------- scroll handler -------------------- //
  const handleScrollImmediate = useCallback(async () => {
    if (currentPage >= totalPage) {
      return;
    }

    setLoading(true);

    try {
      const nextPage = currentPage + 1;
      const result = await fetchBestSellerData({
        currentPage: nextPage,
        sequenceId: currentSequenceId.toString(),
      });

      if (result?.success && result?.data?.data?.data) {
        setDatas((prev: any) => [...prev, ...result?.data?.data?.data]);
        setCurrentPage(nextPage);
        setLastPage(datas.length >= result?.data?.data?.total);
      }
    } catch (error) {
      console.error('加载更多数据失败：', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, totalPage, currentSequenceId, datas, lastPage]);

  const debouncedHandleScroll = useMemo(
    () => debounce(handleScrollImmediate, 200),
    [handleScrollImmediate],
  );
  async function handleFetch(sequenceId: string) {
    try {
      const res = await fetchBestSellerData({
        sequenceId: sequenceId,
        currentPage: 1,
      });

      const { data = [], total, pageSize } = res?.data?.data || {};
      // Set all states at once
      setCurrentPage(1);
      setDatas(data);
      setLastPage(datas.length >= total);
      setTotalPage(pageSize);
    } catch (error) {
      console.error('Error fetching best seller data:', error);
      bestSellersBusinessLogger({
        eventName: BESTSELLERS_EVENT_NAMES.SIDEBAR_LOAD_FAIL,
        message: {
          error: error,
        },
      });
    }
  }

  const trackRecommendCard = {
    rankCardExpo: EXPOSURE_RECORD.Activity_BestSellersHome_RankCard_Expo,
    rankCardFeedsExpo: EXPOSURE_RECORD.Activity_BestSellersHome_ProductExpo,
    lookMoreExpo: EXPOSURE_RECORD.Activity_BestSellersHome_LookMoreExpo,
    rankCardClick: (args: any) => sendClickTrack.Activity_BestSellersHome_LookMore(args),
    rankCardFeedsClick: (args: any) => sendClickTrack.Activity_BestSellersHome_Product(args),
  };

  const bannerTrack = {
    tabExpo: EXPOSURE_RECORD.Activity_BestSellersHome_Tab_Expo,
    tabMoreExpo: EXPOSURE_RECORD.Activity_BestSellerHome_Tab_More_Expo,
    drawerMoreExpo: EXPOSURE_RECORD.Activity_BestSellerHome_More_Expo,
  };

  return (
    <div className={styles.container} id='white_screen_detection_element'>
      <PageBanner
        bannerDesc={bannerDesc}
        bannerBackGround={bannerBackGround}
        sidebarData={sidebarData}
        handleFetch={handleFetch}
        bannerTrack={bannerTrack}
        setCurrentSequenceId={setCurrentSequenceId}
      />
      <div className={styles.contentContainer}>
        <RecommendCard
          recommendData={datas}
          currencyCode={localInfo?.currencyCode || ''}
          languageCode={localInfo?.languageCode || ''}
          track={trackRecommendCard}
        />
      </div>

      <div ref={sentinelRef} style={{ height: '1px' }}></div>

      {lastPage ? (
        <span className={styles.end}>{t('bestSeller.recommend.endTxt')}</span>
      ) : (
        <>{!loading && <div className={styles.endSpacing}></div>}</>
      )}

      {loading && !lastPage && (
        <div className={styles.moreLoading}>
          <UKLoadingIcon />
          {t('common.pageStatus.loading')}...
        </div>
      )}
    </div>
  );
}
